import { ITextBuilder } from "../../Global/Base/Builders/ITextBuilder";
import { PromiseUtils } from "../../Global/Base/Promises/PromiseUtils";
import { DateUtils } from "../../Global/Base/Utils/DateUtils";
import { registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { FilterDateEdit } from "../UI/UI/XGControls/Filters/AdvancedFilters/FilterDateEdit";
import { Control } from "./Control";
import { IValidatable, IValidationResult } from "./ControlInterfaces";
import { EditableControl } from "./EditableControl";
import { IBooleanDetailValueEvent } from "./IEvent";
import { TimeEdit } from "./TimeEdit";

export class DateTimeEdit extends EditableControl {
    protected _timeEdit: TimeEdit;
    protected _dateEdit: FilterDateEdit;
    protected _originalValue: Date;

    public override isCompositeComponent: boolean = true;

    public override set value(value: any) {
        if (typeof value === "string") {
            const d = new Date(value);
            if (/[0-9]{4}-([0-1][0-2]|0[1-9])-([0-2][0-9]|3[0-1])(T|\s)([0-1][0-9]|2[0-4]):[0-5][0-9]:[0-5][0-9](\.\d+)?$/.test(value)) {
                this._originalValue = DateUtils.fromXsdDateTime(value);

                this._dateEdit.value = this._originalValue;
                this._timeEdit.value = this._originalValue;
            } else if (!isNaN(d.getDate())) {
                this._originalValue = d;

                this._dateEdit.value = this._originalValue;
                this._timeEdit.value = this._originalValue;
            }
        } else {
            this._originalValue = value;
            this._dateEdit.value = value;
            this._timeEdit.value = value;
        }
    }

    public override get value(): any {
        return this._originalValue;
    }

    public override get displayedValue(): ITextBuilder {
        // tady je to dost komplikovany pripad
        // - pokud bude DTE v bunce seznamu a uzivatel preklikne na jiny radek,
        // tak se tato hodnota prenese do needitacniho pole a hodnota musi uzivateli stale davat smysl
        // - ve stejne situaci na datailu musi byt mozne sem (pres tuto property) zpetne vlozit nevalidni hodnotu a vlozit jeji jednotlive casti do jednotlivych komponent

        return {
            dateEdit: this._dateEdit.displayedValue,
            timeEdit: this._timeEdit.displayedValue,
            toString: () => {
                return `${this._dateEdit.displayedValue} ${this._timeEdit.displayedValue}`; // `
            },
        } as ITextBuilder;
    }

    public override set displayedValue(v: ITextBuilder) {
        if (!v)
            return;

        const data = v as { dateEdit: string; timeEdit: string };

        this._dateEdit.displayedValue = data.dateEdit;
        this._timeEdit.displayedValue = data.timeEdit;
    }

    public set controlValue(value: Date) {
        this._originalValue = value;

        this._dateEdit.value = value;
        this._timeEdit.value = value;
    }

    public get controlValue(): Date {
        return this._originalValue;
    }

    protected static override get observedAttributes(): string[] {
        return ["timeformat"].concat(EditableControl.observedAttributes);
    }

    public override focus(options?: FocusOptions): void {
        this._dateEdit.focus(options);
    }

    protected override _constructor(): void {
        super._constructor();

        const tcont: string = this.textContent.trim();
        this.innerHTML = "";

        this.classList.add("DateTimeEdit");
        //this.originalValue = DateUtils.FromXSDDateTime('0100-01-01T00:00:00');

        this._createElement();
        this._setListener();

        if (tcont.length > 0) {
            this.value = tcont;
        }
    }

    protected override connectedCallback(): void {
        super.connectedCallback();

        Control.registerEventListener(this._eventGroup, this._innerElement, "focusout", () => {
            if (!this.isValid) // stav nevalidnosti nektere komponenty se nebude prepisovat stavem, ze jedno pole neni vyplneno
                return;

            if (this._dateEdit.controlValue && !this._timeEdit.controlValue || !this._dateEdit.controlValue && this._timeEdit.controlValue) {
                // je potřeba počkat, až se projeví ztráta focusu
                PromiseUtils.delay(0).then(() => {
                    // pokud ani jedna subkomponenta nemá focus, tak se validuje, jestli jsou obě nevyplněné nebo obě vyplněné
                    if (!this._timeEdit.isFocused && !this._dateEdit.isFocused &&
                        (this._dateEdit.controlValue && !this._timeEdit.controlValue || !this._dateEdit.controlValue && this._timeEdit.controlValue))
                        this.setInvalid("VAL-615396"); // Všechna pole musí být vyplněna
                });
            }
        });

        this.registerEventListener("enabled", (e: IBooleanDetailValueEvent) => {
            this._dateEdit.enabled = e.detail.value;
            this._timeEdit.enabled = e.detail.value;
        });

        this.registerEventListener("isediting", (e: IBooleanDetailValueEvent) => {
            this._dateEdit.isEditing = e.detail.value;
            this._timeEdit.isEditing = e.detail.value;
        });

        this.registerEventListener("focusin", () => {
            this.validate(); // provede se validace, aby se odstranila pripadna hlaska, ze musi byt vyplnena vsechna pole
        });
    }

    protected override attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        super.attributeChangedCallback(attrName, oldVal, newVal);

        if (attrName == "timeformat") {
            this._timeEdit.setAttribute("timeformat", newVal);
        }
    }

    private _createElement(): void {
        this._innerElement = document.createElement("div");
        this.appendChild(this._innerElement);
        this._innerElement.classList.add("DateTimeEditParent");

        this._dateEdit = new FilterDateEdit();
        this._timeEdit = new TimeEdit();
        if (this.hasAttribute("timeformat"))
            this._timeEdit.setAttribute("timeformat", this.getAttribute("timeformat"));

        // jednotlivé podkomponenty mají zakázané vlastní validační tooltipy
        this._dateEdit.errorTooltipDisabled = true;
        this._timeEdit.errorTooltipDisabled = true;

        const tformat = this.getAttribute("timeFormat");
        if (tformat)
            this._timeEdit.setAttribute("timeFormat", tformat);
        this._innerElement.appendChild(this._dateEdit);
        this._innerElement.appendChild(this._timeEdit);
    }

    private _setListener(): void {
        this.afterAttach(() => {
            this._timeEdit.registerEventListener("change", (e) => {
                e.stopPropagation();
                this._valueChanged();
            });

            this._dateEdit.registerEventListener("change", (e) => {
                e.stopPropagation();
                this._valueChanged();
            });

            this._dateEdit.registerEventListener("keypress", (e: KeyboardEvent) => {
                this.callEvent("keypressed", { original: e });
            });

            this._timeEdit.registerEventListener("keypress", (e: KeyboardEvent) => {
                this.callEvent("keypressed", { original: e });
            });

            this._dateEdit.validityChanged.add((_element: IValidatable, validity: IValidationResult) => {
                this._checkValidity(validity);
            });

            this._timeEdit.validityChanged.add((_element: IValidatable, validity: IValidationResult) => {
                this._checkValidity(validity);
            });
        });
    }

    private _valueChanged(): void {
        if (!this.isValid || !this.isGuiValid)
            return;

        if (this._updateOriginalValue())
            this.callEvent("change");
    }

    protected _updateOriginalValue(): boolean {
        const date = this._dateEdit.controlValue;
        const time = this._timeEdit.controlValue;
        const oldOriginalValue = this._originalValue;

        // az kdyz jsou vyplnena obe pole, tak se sklada vysledna hodnota DateTimeEditu
        if (date && time) {
            this._originalValue = new Date(date.getFullYear(), date.getMonth(), date.getDate(), time.getHours(), time.getMinutes(), time.getSeconds(), time.getMilliseconds());
        } else {
            if (date == null && time == null)
                this._originalValue = null;
        }

        let changed = false;
        if ((oldOriginalValue == null && this._originalValue != null) || (oldOriginalValue != null && this._originalValue == null)) changed = true;
        if (oldOriginalValue != null && this._originalValue != null && oldOriginalValue.valueOf() != this._originalValue.valueOf()) changed = true;

        // zmena hodnoty se ohlasuje az kdyz jsou obe casti vyplnene nebo obe prazdne
        return ((time != null && date != null) || (time == null && date == null)) && changed;
    }

    private _checkValidity(validity: IValidationResult): void {
        if (!validity.valid)
            this.setInvalid(validity.invalidLocKey, validity.invalidMessage); // jakmile je jedna subkomponenta nevalidní, tak se znevalidní celek
        else if (this._dateEdit.isValid && this._timeEdit.isValid)
            this.setValid(); // ale zpátky se může zvalidnit až jsou opět validní obě
    }

    public override validate(): boolean {
        this._dateEdit.validate();
        this._timeEdit.validate();
        return this.isValid;
    }
}

registerCustomElement("tsc-DateTimeEdit", DateTimeEdit);
